import { useEffect, useState } from 'react';
import { useAuth } from 'zite-auth-sdk';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import TablePlannerCanvas from '../components/admin/TablePlannerCanvas';
import { getTablePlannerData, getRoomLayouts, GetTablePlannerDataOutputType } from 'zite-endpoints-sdk';

type PlannerData = GetTablePlannerDataOutputType;

export default function FloorPlans() {
  const { user, isLoading } = useAuth();
  const [data, setData] = useState<PlannerData | null>(null);
  const [defaultLayoutId, setDefaultLayoutId] = useState<string | undefined>();
  const [loading, setLoading] = useState(true);

  const isManager = user?.role === 'Manager' || user?.role === 'Admin';

  useEffect(() => {
    if (!user) return;

    Promise.all([
      getTablePlannerData({}),
      getRoomLayouts({}),
    ])
      .then(([plannerData, layoutData]) => {
        const rooms = isManager
          ? plannerData.rooms
          : plannerData.rooms.filter(r => (r as any).visibleToStaff);
        setData({ rooms });

        // Find the default layout for the first active room (if any)
        if (!isManager && rooms.length > 0) {
          const firstRoom = rooms[0];
          const defaultLayout = layoutData.layouts.find(
            l => l.roomId === firstRoom.id && l.isDefault
          );
          if (defaultLayout) setDefaultLayoutId(defaultLayout.id);
        }
      })
      .catch(() => toast.error('Failed to load floor plans'))
      .finally(() => setLoading(false));
  }, [user, isManager]);

  if (isLoading || loading) {
    return (
      <div className="px-4 md:px-8 py-6 max-w-6xl mx-auto space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-96" />
      </div>
    );
  }

  if (!data || data.rooms.length === 0) {
    return (
      <div className="px-4 md:px-8 py-12 text-center text-muted-foreground">
        <p className="text-sm">
          No floor plans available.{' '}
          {isManager
            ? 'Configure rooms in Admin → Venue & Rooms.'
            : 'Ask a manager to enable room visibility in Admin → Venue & Rooms.'}
        </p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="px-4 md:px-8 py-6 max-w-6xl mx-auto">
        <div className="mb-5">
          <h1 className="text-xl font-bold text-foreground">Floor Plans</h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            {isManager
              ? 'Set canonical table positions or create named event layouts'
              : 'View the room layout and create custom layouts for events'}
          </p>
        </div>

        <div className="bg-card border border-border rounded-lg p-4">
          <TablePlannerCanvas
            canSaveCanonical={isManager}
            showCreateLayoutFlow={!isManager}
            defaultLayoutId={!isManager ? defaultLayoutId : undefined}
          />
        </div>
      </div>
    </div>
  );
}
